import { Service } from "./Service";
import { PageBodyService } from "./PageBody";
import { PageHead } from "./PageHead";
import { AppInfoBus } from "./AppInfoBus";
import { AppStack } from "./AppStack";
import { App as AppService } from "./App";
import { User as UserService } from "./User";
import { HARouter } from "../Route/Router";
import { HANotification } from "../Notification/HANotification";
import { HAApp } from "../Entity/HAApp";
import { HANavigationApp } from "../Entity/HANavigationApp";
import { Entity } from "../Entity/Entity";
import { Tag } from "../HTML/Tag";
import type { App } from "../Entity/App";
import { bindDocumentClickListener, initScopes } from "../Controller/Page/ScopeController";
import { initInteractions } from "../Controller/Page/InteractionController";
import { guardedNavigate, HistoryCursor } from "../Controller/Page/LeaveGuard";
import { toMap, timeout, log, getAppService, setAppService, setService4App, setAppId } from "../../../HA";

export class WebPage extends Service {

    public showModal(data: Map<any, any>): void {
        data.set("_isModal", true);
        this.reloadAppNow(data);  // une modale ne quitte pas l'écran courant
    }

    public showApp2(app: App): void {
        guardedNavigate(() => {
            this.addApp(app);
            this.showAppNow(toMap({id: app.getId()}));
        });
    }

    public showAppMenu(data: any): void {
        this.getBody().showAppMenu(data);
    }

    public reloadNav(): void {
        const appData = this.getBody().getAppData();
        appData.set("services", this.getServicesForApp());

        // Garde ha/443b : une page noNav (ex. /login) n'a pas de navigationId.
        // Sans ce garde, on POSTait `/ha/app id=undefined` (→ 404 avalé par le
        // catch, mais requête gaspillée + bruit console). On ne fetch la nav que
        // si une navigationId est réellement présente.
        const navId = appData.get("navigationId");
        // `nonav` : le SSR a déjà constaté qu'aucune app navigation n'existe
        // (nav inline) — re-demander produirait un 404 console à chaque boot.
        if (appData.get("controllerName") && navId && !appData.get("nonav")) {
            this.findApp(toMap({id: navId}), true)
                .then((nav: HANavigationApp) => {
                    this.getBody().reloadNav(nav);
                })
                // Sans catch, un nav restreint (ex. ha.dashboard.navigation
                // pour un anon → 404 "Ressource introuvable!") devient une
                // unhandled rejection visible côté browser comme
                // `pageerror: Map(3)`. La nav protégée est un cas normal,
                // pas une erreur runtime — on swallow silencieusement.
                .catch(() => { /* nav indisponible (perms ou 404) */ });
        }
    }

    public reloadApp(data?: Map<any, any>): void {
        guardedNavigate(() => this.reloadAppNow(data));
    }

    protected reloadAppNow(data?: Map<any, any>): void {
        let id: any;
        if (!data || !data.has("id")) {
            data = this.getActiveAppData();
            id = data.get("_id") || data.get("id");
            const app = this.get("curApp");
            if (app) {
                app.savePosition();
                data.set("position", app.getPosition());
                data.delete("app");
            }
        }

        id = id || data.get("_id") || data.get("id");
        this.removeApp(id, false);
        const history = this.getHistory();
        const last = history.get(history.size - 1);
        if (last && this.isSameApp(last, data)) {
            history.delete(history.size - 1);
        }
        timeout(() => this.showAppNow(data), 5);
    }

    public toggleApp(data: Map<any, any>): void {
        guardedNavigate(() => {
            if (this.isActive(data.get("_id")) || this.isActive(data.get("id"))) {
                this.showPreviousNow(false);
            } else {
                this.showAppNow(data);
            }
        });
    }

    public showPrevious(clean = true): void {
        guardedNavigate(() => this.showPreviousNow(clean));
    }

    protected showPreviousNow(clean = true): void {
        if (!clean || this.clean()) {
            const history = this.getHistory();
            if (history.size <= 1) { return; }
            history.delete(history.size - 1);
            const data = history.get(history.size - 1);
            history.delete(history.size - 1);
            this.switchToApp(data);
        }
    }

    public toggleQMenu(): void {
        if (this.get("qmenu")) {
            this.get("qmenu")();
        }
    }

    public setQMenu(qmenu: () => void): void {
        this.addToData("qmenu", qmenu);
    }

    /** Navigation SPA : consulte les gardes de sortie (HA-276) avant de partir. */
    public showApp(data: Map<any, any>): void {
        guardedNavigate(() => this.showAppNow(data));
    }

    protected showAppNow(data: Map<any, any>): void {
        const history = this.getHistory();
        const last = history.get(history.size - 1);
        if (!last || !this.isSameApp(last, data)) {
            if (data.get("id") === "main") {
                data = this.get("mainApp");
            }
            this.switchToApp(data);
        }
    }

    protected isSameApp(currentData: Map<any, any>, newData: Map<any, any>): boolean {
        if (currentData.get("id") !== newData.get("id")) { return false; }

        if (newData.has("_id") || currentData.has("_id")) {
            return currentData.get("_id") === newData.get("_id");
        }
        if (newData.has("fid") || currentData.has("fid")) {
            return currentData.get("fid") === newData.get("fid");
        }
        return true;
    }

    protected getServicesForApp() {
        return {
            "ha.service.appManager": this.getBody().getAppManager(),
            "ha.service.user":       this.getUserService(),
            "ha.service.webpage":    this.getBaseApi(),
            "router":                new HARouter(),
        };
    }

    protected getBaseApi() {
        return {
            isActive:             (id: string)             => this.isActive(id),
            notification:         ()                       => this.getNotification(),
            reloadApp:            (data?: any)             => this.reloadApp(data),
            reloadNav:            ()                       => this.reloadNav(),
            reloadPreviousApp:    ()                       => guardedNavigate(() => this.reloadPreviousApp()),
            removeAll:            ()                       => this.removeAll(),
            removeApp:            (id: string)             => guardedNavigate(() => this.removeApp(id)),
            removeCurrentApp:     ()                       => guardedNavigate(() => this.removeCurrentApp()),
            replaceAppId:         (id: any, newId: any)    => this.replaceAppId(id, newId),
            setMainApp:           (d: any)                 => this.setMainApp(d),
            showApp:              (data: any)              => this.showApp(data),
            showAppMenu:          (data: any)              => this.showAppMenu(data),
            showFSApp:            (app: App)               => this.showApp2(app),
            showModal:            (data: any)              => this.showModal(data),
            showPreviousApp:      ()                       => this.showPrevious(),
            toggleQMenu:          ()                       => this.toggleQMenu(),
            setQMenu:             (qmenu: () => void)      => this.setQMenu(qmenu),
            subscribeToAppInfo:   (func: () => void)       => this.subscribeToAppInfo(func),
            unsubscribeToAppInfo: (id: string)             => this.unsubscribeToAppInfo(id),
            toggleApp:            (data: any)              => this.toggleApp(data),
        };
    }

    protected replaceAppId(id: any, newId: any): void {
        let last = this.getActiveAppData();
        if (!last) {
            const history = this.getHistory();
            for (let i = history.size; i >= 0; --i) {
                const app: Map<any, any> = history.get(i);
                if (app && app.get("id") === id) {
                    last = app;
                    break;
                }
            }
        }
        if (last) {
            last.set("id", newId);
        }
    }

    protected subscribeToAppInfo(func: (info: Map<string, any>) => void): string[] {
        return this.getAppInfoBus().subscribe(func, this.get("currAppId") || "main");
    }

    protected unsubscribeToAppInfo(subscriptionId: string): void {
        this.getAppInfoBus().unsubscribe(subscriptionId);
    }

    protected notifyAppInfo(info: Map<string, any>): void {
        this.getAppInfoBus().notify(info);
    }

    protected getServicesForBody() {
        return {
            "ha.service.webpage": this.getBaseApi(),
            "router":             new HARouter(),
        };
    }

    protected getNotification() {
        return this.get("ha.notification");
    }

    protected removeCurrentApp(): void {
        const data = this.getActiveAppData();
        if (!data) { return; }
        if (this.getHistory().size === 1) {
            this.reloadAppNow(toMap({id: "index"}));
            return;
        }
        this.removeApp(data.get("_id") || data.get("id"));
    }

    protected reloadPreviousApp(): void {
        this.removeCurrentApp();
        timeout(() => this.reloadAppNow(), 100);
    }

    protected removeApp(id: string, showPrevious: boolean = true): void {
        if (showPrevious && this.isActive(id)) {
            this.showPreviousNow();
        }
        const app: App | undefined = this.getAppStack().get(id);
        if (app) {
            this.getAppStack().delete(id);
            getAppService().removeApp(id);
            this.notifyAppInfo(toMap({id: app.getLocalId(), s: -1}) as Map<string, any>);
        }
    }

    protected removeAll(): void {
        this.getAppStack().clear();
    }

    protected isActive(id?: string): boolean {
        if (!id) { return false; }
        const last = this.getActiveAppData();
        return last && (last.get("_id") === id || last.get("id") === id);
    }

    protected getActiveAppData(): Map<any, any> | undefined {
        const history = this.getHistory();
        return history.get(history.size - 1);
    }

    protected addApp(app: App): void {
        this.getAppStack().add(app);
    }

    protected getAppStack(): AppStack {
        if (this.hasNot("apps")) {
            this.addToData("apps", new AppStack());
        }
        return this.get("apps");
    }

    protected getUserService() {
        if (this.hasNot("ha.service.user")) {
            this.addToData("ha.service.user", new UserService({
                services: {
                    "ha.service.webpage": this.getBaseApi(),
                    "router":             new HARouter(),
                },
            }));
        }
        return this.get("ha.service.user");
    }

    protected findApp(data: Map<any, any>, isNavigation?: boolean) {
        // ha/508 — Clé d'instance : `_id` (composite réf) prime sur `id`. Sans
        // ça, deux navs vers le même pageId détail (références différentes)
        // court-circuitaient ici sur la même instance AVANT l'aller-retour
        // serveur. Aligné avec isSameApp/isActive/removeCurrentApp qui priment
        // déjà `_id`. Pas de `_id` → `id` : comportement historique inchangé.
        const id  = data.get("_id") || data.get("id");
        const app: App | undefined = this.getAppStack().get(id);
        if (app) {
            data.set("ac", (data.get("ac") || 1) + 1);
            return Promise.resolve(app);
        }
        const lid = Entity.genId();
        data.set("ac", 1);
        data.set("lid", lid);
        if (!isNavigation) {
            this.addToData("currAppId", lid);
        }
        return getAppService().findById(data, isNavigation).then((app2: App) => {
            if (data.has("position") && app2) {
                app2.setPosition(data.get("position"));
            }
            app2.setLocalId(data.get("lid"));
            return app2;
        });
    }

    protected getHistoryCursor(): HistoryCursor {
        if (this.hasNot("ha.history.cursor")) { this.addToData("ha.history.cursor", new HistoryCursor(window.history)); }
        return this.get("ha.history.cursor");
    }

    protected getHistory(): Map<number, any> {
        return this.get("ha.history");
    }

    protected getHead(): PageHead {
        return this.get("ha.service.page.head");
    }

    protected getBody(): PageBodyService {
        return this.get("ha.service.page.body");
    }

    protected showAppInternal(app: App, data: Map<any, any>): void {
        this.addApp(app);
        const history = this.getHistory();
        if (app.isCachable()) {
            history.set(history.size, data);
        }
        if (app.hasUrl()) {
            // ha/508 — Le serveur renvoie l'URL STATIQUE du manifest
            // (`/admin/agency/devis-detail`, sans la référence). Si le clic a
            // fourni un `_url` porteur (`?reference=D-100010`), on l'utilise pour
            // l'historique : adresse correcte + refresh résolvant le bon record.
            const navUrl = data.get("_url") || app.getUrl();
            this.getHistoryCursor().record(data, app.getName(), navUrl, data.get("ac") > 1);
        }
        this.getHead().applyHead(app);
        this.getBody().showApp(app);
        this.addToData("curApp", app);
        app.setViewed(true);
        this.notifyAppInfo(toMap({id: app.getLocalId(), s: 1}) as Map<string, any>);
    }

    protected switchToApp(data: Map<any, any>): void {
        if (data.get("app")) {
            this.addApp(data.get("app"));
        }
        this.findApp(data).then((app: App) => {
            const id = app.getId();
            if (id && id !== data.get("id")) {
                data.set("id", id);
            }
            this.showAppInternal(app, data);
        }).catch((e) => {
            const err = (e instanceof Map) && e.get("error") ? e.get("error") : e;
            getAppService().processAction(toMap(err));
        });
    }

    /**
     * HA-203 — Décision de boot de l'app principale, extraite d'initialize pour
     * être testable : avec un préchargement `ssr-app` (posé par le SSR et extrait
     * par PageBodyService.getAppData), l'app est créée dans le cache repository
     * puis servie par showApp — AUCUN re-POST /ha/app. Sans préchargement,
     * reloadApp fait l'aller-retour serveur (comportement historique).
     */
    protected bootApp(appData: Map<any, any>): void {
        if (appData.get("controllerName")) {
            if (appData.get("_1")) {
                this.getNotification().activateDebugMode();
            } else {
                this.getNotification().deactivateDebugMode();
            }

            if (appData.get("noReload")) {
                const element    = document.querySelector("[apps]");
                const navElement = document.querySelector("nav");
                const appDataClone = toMap(appData);
                appDataClone.set("tag", new Tag({element: navElement}));
                appData.set("tag", new Tag({element: element.firstChild}));
                appData.set("navigation", new HANavigationApp(appDataClone));
                const app = new HAApp(appData);
                this.getHead().applyHead(app);
                this.getBody().showApp(app);
                this.addToData("curApp", app);
                return;
            }

            const hasPreload = !!appData.get("ssr-app");

            if (hasPreload) {
                if (appData.get("ssr-nav")) {
                    const navId = appData.get("navigationId");
                    const navData = toMap(JSON.parse(appData.get("ssr-nav")));
                    if (navId) navData.set("id", navId);
                    getAppService().createApp(toMap({id: navId}), navData, true);
                    appData.delete("ssr-nav");
                }
                getAppService().createApp(appData, toMap(JSON.parse(appData.get("ssr-app"))), false);
                appData.delete("ssr-app");
            }

            if (hasPreload && appData.get("id")) {
                // Pre-loaded data is in the repository cache — use showApp which
                // checks cache first instead of reloadApp which would clear it.
                timeout(() => this.showAppNow(appData), 5);
            } else if (appData.get("id")) {
                this.reloadAppNow(appData);
            } else {
                this.showAppInternal(new HAApp(appData), appData);
            }
            this.reloadNav();
        } else {
            this.showAppNow(appData);
        }
    }

    protected initialize(data: any): void {
        super.initialize(data);
        this.addToData("ha.history",      new Map());
        this.addToData("ha.history.cursor", new HistoryCursor(window.history));
        this.addToData("ha.notification", new HANotification({debug: false}));

        const pageBody = new PageBodyService({services: this.getServicesForBody()});
        this.addToData("ha.service.page.body", pageBody);
        this.addToData("ha.service.user", new UserService({
            services: {
                "ha.service.webpage": this.getBaseApi(),
                "router":             new HARouter(),
            },
        }));

        setAppService(new AppService(this.getServicesForApp()));
        setService4App(this.getServicesForApp());
        this.addToData("ha.service.page.head", new PageHead());

        const appData = pageBody.getAppData();
        if (appData.get("controllerName") !== "LoginService") {
            this.addToData("mainApp", appData);
        }

        if (appData.has("_m")) {
            log({m: appData.get("_m"), d: 5000});
        }
        setAppId(appData.get("haId"));

        this.bootApp(appData);

        // Retour/avance navigateur : gardes de sortie consultées ; un refus ramène l'adresse
        // sur l'écran resté affiché (HistoryCursor). Entrée initiale (sans état) acceptée :
        // rechargement de l'adresse atteinte (showApp(null) planterait).
        window.onpopstate = (event: PopStateEvent) => {
            this.getHistoryCursor().pop(event.state, (data) => this.showAppNow(data), () => window.location.reload());
        };

        if (this.has("jsNotification")) {
            this.subscribeToAppInfo(this.get("jsNotification"));
        }

        // ScopeController : un listener global capture-phase délègue les clicks
        // sur [data-ha-set] au ScopeController du scope ancêtre (lazy-binding).
        // Voir ScopeController.ts pour les conventions data-ha-scope/render/set.
        bindDocumentClickListener();
        initScopes();
        initInteractions();
        this.subscribeToAppInfo(() => {
            initScopes();
            initInteractions();
        });
    }

    private clean(): boolean {
        const apps: AppStack = this.getAppStack();
        apps.forEach((app) => {
            // ha/508 — clé d'instance (cohérent avec AppStack/findApp).
            const key = app.getStackKey();
            if (!this.isActive(key) && app.isNotCachable() && app.isViewed()) {
                this.removeApp(key);
            }
        });

        const histories: Map<any, any> = this.getHistory();
        const newHistories = new Map();
        histories.forEach((data: Map<any, any>, key: string) => {
            const id: string  = data.get("_id") || data.get("id");
            const app: App | undefined = apps.get(id);
            if (app && app.isNotNavigable() && app.isViewed()) {
                histories.delete(key);
            } else {
                newHistories.set(newHistories.size, data);
            }
        });

        this.addToData("ha.history", newHistories);
        return true;
    }

    private setMainApp(data: any): void {
        this.addToData("mainApp", data);
    }

    private getAppInfoBus(): AppInfoBus {
        if (this.hasNot("appInfoBus")) {
            this.addToData("appInfoBus", new AppInfoBus());
        }
        return this.get("appInfoBus");
    }
}
